import type { LandingContent, LandingTeamMemberContent } from '../content/landingContent.js';
import type { StaticPageAudience, StaticPageContent, StaticPageKey } from '../content/staticPageContent.js';

const cloneEditorValue = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const isEditorAsset = (source: unknown): source is string =>
  typeof source === 'string' && (source.includes('/editor-assets/') || source.startsWith('data:'));

const backfillGlobusJobMedia = (
  target: StaticPageContent,
  defaults: StaticPageContent,
  profession: 'bakery' | 'butcher' | 'fresh-counter' | 'gastronomy',
) => {
  const targetJob = target.globusJobs?.find(job => job.profession === profession);
  const defaultJob = defaults.globusJobs?.find(job => job.profession === profession);
  if (!targetJob || !defaultJob) return;

  if (!isEditorAsset(targetJob.video) && typeof defaultJob.video === 'string') targetJob.video = defaultJob.video;
  if (!isEditorAsset(targetJob.poster) && typeof defaultJob.poster === 'string') targetJob.poster = defaultJob.poster;
  targetJob.captionTracks = defaultJob.captionTracks.map(defaultTrack => {
    const storedTrack = targetJob.captionTracks?.find(track => track.srclang === defaultTrack.srclang);
    return {
      ...cloneEditorValue(defaultTrack),
      ...cloneEditorValue(storedTrack ?? {}),
      src: storedTrack?.src || (typeof defaultTrack.src === 'string' ? defaultTrack.src : ''),
    };
  });
};

export const backfillGlobusBakeryMedia = (target: StaticPageContent, defaults: StaticPageContent) =>
  backfillGlobusJobMedia(target, defaults, 'bakery');

export const backfillGlobusButcherMedia = (target: StaticPageContent, defaults: StaticPageContent) =>
  backfillGlobusJobMedia(target, defaults, 'butcher');

export const backfillGlobusGastronomyMedia = (target: StaticPageContent, defaults: StaticPageContent) =>
  backfillGlobusJobMedia(target, defaults, 'gastronomy');

export const backfillGlobusFreshCounterMedia = (target: StaticPageContent, defaults: StaticPageContent) =>
  backfillGlobusJobMedia(target, defaults, 'fresh-counter');

export const backfillFeaturedGroupPhoto = (
  target: StaticPageContent,
  defaults: StaticPageContent,
  pageKey: StaticPageKey,
) => {
  if (pageKey === 'globus') {
    target.heroGallery = cloneEditorValue(defaults.heroGallery);
  }

  if (pageKey === 'ueber-vivaporta') target.heroImage = defaults.heroImage;
};

const fanTestimonialKeys = ['heroFanApplicant', 'heroFanCompany'] as const;

export const syncFanCardImages = (target: LandingContent, defaults: LandingContent) => {
  for (const key of fanTestimonialKeys) {
    const targetCards = target[key]?.cards;
    const defaultCards = defaults[key]?.cards;
    if (!Array.isArray(targetCards) || !Array.isArray(defaultCards)) continue;
    targetCards.forEach((card, index) => {
      const fallbackCard = defaultCards[index];
      if (!card || !fallbackCard) return;
      card.image = fallbackCard.image;
      card.imageAlt = fallbackCard.imageAlt;
    });
  }
};

export const backfillFanTestimonials = (target: LandingContent, defaults: LandingContent) => {
  for (const key of fanTestimonialKeys) {
    const targetCards = target[key]?.cards;
    const defaultCards = defaults[key]?.cards;
    if (!Array.isArray(targetCards) || !Array.isArray(defaultCards)) continue;
    targetCards.forEach((card, index) => {
      const fallbackCard = defaultCards[index];
      if (!card || !fallbackCard) return;
      if (!card.quote && fallbackCard.quote) card.quote = fallbackCard.quote;
      if (!card.quoteAuthor && fallbackCard.quoteAuthor) card.quoteAuthor = fallbackCard.quoteAuthor;
      if (!card.quoteRole && fallbackCard.quoteRole) card.quoteRole = fallbackCard.quoteRole;
    });
  }
};

const replaceLegacyBrandNameInVisibleCopy = (value: unknown): void => {
  if (Array.isArray(value)) {
    value.forEach(replaceLegacyBrandNameInVisibleCopy);
    return;
  }
  if (!value || typeof value !== 'object') return;

  const record = value as Record<string, unknown>;
  for (const [key, entry] of Object.entries(record)) {
    if (typeof entry === 'string') {
      if (/^(?:https?:|mailto:|tel:|data:|\/)/iu.test(entry)) continue;
      record[key] = entry.replace(/\bViva\s*Porta[\s-]*Medical\b/giu, 'Viva Porta');
      continue;
    }
    replaceLegacyBrandNameInVisibleCopy(entry);
  }
};

export const normalizeLandingPresentation = (target: LandingContent, defaults: LandingContent) => {
  const targetCards = target.heroFanCompany?.cards;
  const defaultCards = defaults.heroFanCompany?.cards;
  if (Array.isArray(targetCards) && Array.isArray(defaultCards)) {
    targetCards.forEach((card, index) => {
      const fallback = defaultCards[index];
      if (!fallback) return;
      card.href = fallback.href;
      card.quote = fallback.quote;
      card.quoteAuthor = fallback.quoteAuthor;
      card.quoteRole = fallback.quoteRole;
    });
  }

  if (target.intro && defaults.intro) target.intro.text = defaults.intro.text;
  if (target.values?.bridgeAction) target.values.bridgeAction.href = '/ueber-vivaporta';
  replaceLegacyBrandNameInVisibleCopy(target);
};

export const normalizeStaticPagePresentation = (
  target: StaticPageContent,
  defaults: StaticPageContent,
  audience: StaticPageAudience,
  pageKey: StaticPageKey,
) => {
  if (pageKey === 'globus') {
    target.heroGallery = cloneEditorValue(defaults.heroGallery);
    if (target.cta && defaults.cta) {
      target.cta.image = defaults.cta.image;
      target.cta.imageAlt = defaults.cta.imageAlt;
    }
  }

  if (pageKey === 'ueber-vivaporta') {
    target.heroImage = defaults.heroImage;
    const targetBackground = target.sections?.[0];
    const defaultBackground = defaults.sections?.[0];
    if (targetBackground && defaultBackground) {
      targetBackground.image = defaultBackground.image;
      targetBackground.imageAlt = defaultBackground.imageAlt;
    }
  }

  if (pageKey === 'impressum') {
    target.eyebrow = defaults.eyebrow;
    target.title = defaults.title;
    target.intro = defaults.intro;
    target.cards = cloneEditorValue(defaults.cards);
  }

  // Keep both audience variants free of the retired sub-brand in all visible text while preserving
  // editor-managed links and media sources.
  if (audience === 'applicant' || audience === 'company') replaceLegacyBrandNameInVisibleCopy(target);
};

const isEditorManagedImage = (image: string | undefined): boolean =>
  typeof image === 'string' && (image.startsWith('/editor-assets/') || image.startsWith('data:image/'));

const syncTeamMemberDefaults = (member: LandingTeamMemberContent, fallbackMember: LandingTeamMemberContent) => {
  if (!isEditorManagedImage(member.image)) member.image = fallbackMember.image;
  if (member.story === undefined && fallbackMember.story !== undefined) member.story = fallbackMember.story;
  if (member.motivation === undefined && fallbackMember.motivation !== undefined) member.motivation = fallbackMember.motivation;
  if (member.linkedinUrl === undefined && fallbackMember.linkedinUrl !== undefined) member.linkedinUrl = fallbackMember.linkedinUrl;
};

const syncRequestedTeamLinkedIn = (member: LandingTeamMemberContent) => {
  const normalizedName = member.name.toLocaleLowerCase('de');
  if (normalizedName.includes('murat')) {
    member.linkedinUrl = 'https://www.linkedin.com/in/murat-v-113bb912/';
  } else if (normalizedName.includes('kirstin')) {
    member.linkedinUrl = 'https://www.linkedin.com/in/kirstin-w-43264a4/';
  } else if (normalizedName.includes('serife') || normalizedName.includes('ergün') || normalizedName.includes('ergun')) {
    delete member.linkedinUrl;
  }
};

export const backfillTeamMemberLinks = (target: LandingContent, defaults: LandingContent) => {
  const targetMembers = target.team?.members;
  const defaultMembers = defaults.team?.members;
  if (!Array.isArray(targetMembers) || !Array.isArray(defaultMembers)) return;
  targetMembers.forEach(member => {
    const fallbackMember = defaultMembers.find(defaultMember => defaultMember.name === member.name);
    if (fallbackMember) syncTeamMemberDefaults(member, fallbackMember);
    syncRequestedTeamLinkedIn(member);
  });
};

export const backfillLandingTeamMembers = (target: LandingContent, defaults: LandingContent) => {
  const targetMembers = target.team?.members;
  const defaultMembers = defaults.team?.members;
  if (!Array.isArray(targetMembers) || !Array.isArray(defaultMembers)) return;
  for (const defaultMember of defaultMembers) {
    if (!targetMembers.some(member => member.name === defaultMember.name)) {
      targetMembers.push(cloneEditorValue(defaultMember));
    }
  }
};

export const normalizeLaborShortageCopy = (target: LandingContent, defaults: LandingContent) => {
  const legacyLaborTitles = new Set([
    'Wenn offene Stellen zum gesellschaftlichen Risiko werden.',
    'Wenn offene Stellen zum Risiko werden.',
    'Wenn offene Stellen nicht besetzt werden, wird aus Personalbedarf ein gesellschaftliches Problem',
    'Wenn offene Stellen nicht besetzt werden, wird aus Personalbedarf ein gesellschaftliches Problem.',
  ]);
  if (target.laborShortage && legacyLaborTitles.has(target.laborShortage.title)) {
    target.laborShortage.title = defaults.laborShortage.title;
    target.laborShortage.titleHighlight = defaults.laborShortage.titleHighlight;
  }
};

export const normalizeMissionLandingCopy = (target: LandingContent, defaults: LandingContent) => {
  if (target.intro && defaults.intro) {
    target.intro.eyebrow = defaults.intro.eyebrow;
    target.intro.title = defaults.intro.title;
    target.intro.titleHighlight = defaults.intro.titleHighlight;
    target.intro.text = defaults.intro.text;
    target.intro.metrics = cloneEditorValue(defaults.intro.metrics);
  }

  if (target.industryOverview && defaults.industryOverview) {
    target.industryOverview.title = defaults.industryOverview.title;
    target.industryOverview.titleHighlight = defaults.industryOverview.titleHighlight;
    target.industryOverview.text = defaults.industryOverview.text;
  }

  if (target.laborShortage && defaults.laborShortage) {
    target.laborShortage.title = defaults.laborShortage.title;
    target.laborShortage.titleHighlight = defaults.laborShortage.titleHighlight;
    target.laborShortage.text = defaults.laborShortage.text;
    target.laborShortage.points = cloneEditorValue(defaults.laborShortage.points);

    // Published TR/EN projects may still contain the German statistic copy from an older content
    // version. Keep editor-managed links intact, but always take visible copy and locale-specific
    // number formatting from the current locale defaults.
    if (target.locale !== 'de') {
      target.laborShortage.stats = defaults.laborShortage.stats.map((fallbackStat, index) => ({
        ...cloneEditorValue(fallbackStat),
        sourceHref: target.laborShortage.stats[index]?.sourceHref ?? fallbackStat.sourceHref,
      }));
      target.laborShortage.sources = defaults.laborShortage.sources.map((fallbackSource, index) => ({
        ...cloneEditorValue(fallbackSource),
        href: target.laborShortage.sources[index]?.href ?? fallbackSource.href,
      }));
    }
  }

  if (target.values && defaults.values) {
    target.values.bridgeAction = cloneEditorValue(defaults.values.bridgeAction);
  }
};

export const normalizePartnerNetworkContent = (target: LandingContent, defaults: LandingContent) => {
  if (!Array.isArray(target.partners?.items) || !Array.isArray(defaults.partners?.items)) return;
  target.partners.items.forEach(item => {
    const fallback = defaults.partners.items.find(defaultItem => defaultItem.name === item.name);
    if (!fallback) return;
    item.description = fallback.description;
    item.href = fallback.href;
    item.logo = fallback.logo;
    item.alt = fallback.alt;
  });
};

export const normalizeGlobusPartnerContent = (target: LandingContent, defaults: LandingContent) => {
  if (!target.globusPartner || !defaults.globusPartner) return;
  const legacyTitles = new Set([
    'Mit Globus entstehen neue Einstiegspunkte für internationale Fachkräfte.',
    'Globus ile uluslararası uzmanlar için yeni başlangıç noktaları oluşuyor.',
    'With Globus, new entry points for international professionals are taking shape.',
    'With Globus, new entry points for international professionals are emerging.',
  ]);
  if (legacyTitles.has(target.globusPartner.title)) target.globusPartner.title = defaults.globusPartner.title;
};

// Collapses brand-name spacing ("Viva Porta" vs "VivaPorta") and whitespace so a published German
// quote still matches the current code default even if it was stored with the brand written together.
const canonicalizeQuote = (value: string | undefined) =>
  (value ?? '')
    .toLowerCase()
    .replace(/viva\s*porta/g, 'vivaporta')
    .replace(/\s+/g, ' ')
    .trim();

export const normalizeStoryContent = (target: LandingContent, defaults: LandingContent, germanDefaults: LandingContent) => {
  if (!target.story || !defaults.story) return;
  const legacyTitles = new Set([
    'Echte Erfahrungen statt austauschbarer Referenzen.',
    'Gerçek geri bildirimler için hazırlanmış alan.',
    'A prepared space for real feedback.',
    'Değiştirilebilir referanslar değil, gerçek deneyimler.',
    'Real experiences instead of interchangeable references.',
  ]);
  if (legacyTitles.has(target.story.title)) {
    target.story.title = defaults.story.title;
    target.story.titleHighlight = defaults.story.titleHighlight;
    target.story.text = defaults.story.text;
  }

  const targetTestimonials = target.story.testimonials;
  const defaultTestimonials = defaults.story.testimonials;
  const germanTestimonials = germanDefaults.story?.testimonials;
  const isLocalizedTarget = defaults !== germanDefaults;
  if (!Array.isArray(targetTestimonials) || !Array.isArray(defaultTestimonials)) return;
  targetTestimonials.forEach((testimonial, index) => {
    const fallback = defaultTestimonials[index];
    if (!fallback) return;
    // A non-German locale whose card still carries the German default text is stale inherited content
    // (the active TR/EN content historically spread the German testimonials). Replace its localized
    // fields with the locale default; genuine per-locale edits differ from the German default and stay.
    const german = germanTestimonials?.[index];
    if (isLocalizedTarget && german && canonicalizeQuote(testimonial.quote) === canonicalizeQuote(german.quote)) {
      testimonial.quote = fallback.quote;
      testimonial.author = fallback.author;
      testimonial.role = fallback.role;
      testimonial.imageAlt = fallback.imageAlt;
      testimonial.logoAlt = fallback.logoAlt;
    }
    const isLegacyPartnerCopy = testimonial.quote === 'Gute Vermittlung beginnt für uns dort, wo Menschen vorbereitet, verstanden und zuverlässig begleitet werden.';
    const isLegacyPlaceholder =
      testimonial.author === 'Viva Porta Redaktion' || testimonial.role === 'Platzhalter - im Editor ersetzen' || testimonial.author === 'Placeholder for real testimonial' || testimonial.author === 'Gerçek testimonial için yer tutucu';
    if (isLegacyPlaceholder || isLegacyPartnerCopy) {
      testimonial.quote = fallback.quote;
      testimonial.author = fallback.author;
      testimonial.role = fallback.role;
    }
    if (testimonial.author === 'Gerd Schneider' && !isEditorManagedImage(testimonial.image)) {
      testimonial.image = fallback.image;
      testimonial.imageAlt = fallback.imageAlt;
    }
  });
};

export const hasLegacyLocalizedLandingContent = (target: LandingContent) => {
  const hasGastroLink = (items: Array<{ href?: string }> | undefined) => Array.isArray(items) && items.some(item => item.href === '/branchen/gastro');
  const hasLegacyHeroFan = hasGastroLink(target.heroFanApplicant?.cards) || hasGastroLink(target.industryOverview?.items) || (Array.isArray(target.heroFanApplicant?.cards) && target.heroFanApplicant.cards.length > 3);
  const hasLegacyNav = hasGastroLink(target.nav) || hasGastroLink(target.footer?.links);
  const hasLegacyCopy = [...(target.hero?.bullets ?? []), target.heroFanApplicant?.intro, target.heroFanCompany?.intro, target.intro?.text].some(text => /gastro|gastronom|hospitality/i.test(text ?? ''));

  return hasLegacyHeroFan || hasLegacyNav || hasLegacyCopy;
};

const visibleIndustryHrefOrder = ['/branchen/pflege', '/branchen/einzelhandel', '/branchen/handwerk'] as const;

const visibleIndustryOrder = <T extends { href: string }>(items: T[] | undefined): T[] => {
  const byHref = new Map((items ?? []).map(item => [item.href, item]));
  return visibleIndustryHrefOrder.map(href => byHref.get(href)).filter((item): item is T => Boolean(item));
};

export const normalizeVisibleIndustries = (target: LandingContent, defaults: LandingContent) => {
  if (target.heroFanApplicant && defaults.heroFanApplicant) {
    target.heroFanApplicant.avatars = cloneEditorValue(defaults.heroFanApplicant.avatars);
    target.heroFanApplicant.cards = cloneEditorValue(visibleIndustryOrder(defaults.heroFanApplicant.cards));
  }

  if (target.industryOverview && defaults.industryOverview) {
    target.industryOverview.items = cloneEditorValue(visibleIndustryOrder(defaults.industryOverview.items));
  }

  if (Array.isArray(target.intro?.metrics) && target.intro.metrics[0]) {
    target.intro.metrics[0].value = '3';
    target.intro.metrics[0].label = target.intro.metrics[0].label
      .replace(/^4\b/u, '3')
      .replace('Pflege, Gastronomie, Handwerk und Einzelhandel', 'Pflege, Einzelhandel und Handwerk')
      .replace('Bakım, gastronomi, zanaat ve perakende', 'Bakım, perakende ve zanaat')
      .replace('care, hospitality, trades and retail', 'care, retail and trades');
  }
};

export const removeProcessMedia = (target: LandingContent) => {
  const targetSteps = target.process?.steps;
  if (!Array.isArray(targetSteps)) return;
  targetSteps.forEach(step => {
    delete step.image;
    delete step.imageAlt;
  });
};

export const softenLegacyQualityClaims = (target: LandingContent, defaults: LandingContent) => {
  const targetStats = target.quality?.stats;
  const defaultStats = defaults.quality?.stats;
  if (Array.isArray(targetStats) && Array.isArray(defaultStats)) {
    const replacements = new Map([
      ['95 %', defaultStats[0]],
      ['24/7', defaultStats[2]],
    ]);
    targetStats.forEach((stat, index) => {
      const replacement = replacements.get(stat.value);
      if (replacement) targetStats[index] = cloneEditorValue(replacement);
    });
  }

  const fairRecruiting = target.quality?.points?.find(point => point.title === 'Faire Anwerbung');
  const defaultFairRecruiting = defaults.quality?.points?.find(point => point.title === 'Faire Anwerbung');
  if (fairRecruiting && defaultFairRecruiting && fairRecruiting.text === 'Wir arbeiten nach den Grundsätzen fairer Anwerbung – transparent, rechtssicher und im Sinne der WHO-Leitlinien.') {
    fairRecruiting.text = defaultFairRecruiting.text;
  }
};

const industryHeroHrefs = [...visibleIndustryHrefOrder];
const legacyIndustryHeroTitles = new Set(['Fachkräfte für Branchen, die Menschen brauchen.', 'İnsana ihtiyaç duyan sektörler için uzmanlar.', 'People for industries built around people.']);

const legacyStaticHeroImageNeedles = ['ChatGPT-Image-23.-Aug.-2025-15_17_53', 'Almanyada-Hemsirelik'];

const legacyIndustryAssetNames = ['industry-care', 'industry-gastro', 'industry-craft', 'industry-retail'];

const isLegacyStaticHeroImage = (image?: string) => {
  if (typeof image !== 'string') return false;
  if (legacyStaticHeroImageNeedles.some(needle => image.includes(needle))) return true;
  return legacyIndustryAssetNames.some(assetName => image.includes(assetName) && !image.includes(`${assetName}-v2`));
};

export const backfillIndustryHeroFanImages = (target: LandingContent, defaults: LandingContent) => {
  const targetSection = target.heroFanApplicant;
  const defaultSection = defaults.heroFanApplicant;
  if (!targetSection || !defaultSection) return;
  if (targetSection.avatars?.some(image => isLegacyStaticHeroImage(image))) {
    targetSection.avatars = cloneEditorValue(defaultSection.avatars);
  }
  for (const defaultCard of defaultSection.cards ?? []) {
    const targetCard = targetSection.cards?.find(card => card.href === defaultCard.href);
    if (targetCard && isLegacyStaticHeroImage(targetCard.image)) {
      targetCard.image = defaultCard.image;
      targetCard.imageAlt = defaultCard.imageAlt;
    }
  }
};

export const backfillIndustryHeroFan = (target: LandingContent, defaults: LandingContent) => {
  const targetCards = target.heroFanApplicant?.cards ?? [];
  const isIndustryHero = industryHeroHrefs.every((href, index) => targetCards[index]?.href === href);
  if (isIndustryHero && !legacyIndustryHeroTitles.has(target.heroFanApplicant?.title ?? '')) {
    backfillIndustryHeroFanImages(target, defaults);
    return;
  }
  target.heroFanApplicant = cloneEditorValue(defaults.heroFanApplicant);
};

const legacyCompanyHeroTitles = new Set(['Mehr Fachkräfte. Mehr Sicherheit. Mehr Team.', 'Daha çok uzman. Daha çok güven. Daha çok ekip.', 'More professionals. More security. More team.']);

export const backfillCompanyHeroFan = (target: LandingContent, defaults: LandingContent) => {
  if (legacyCompanyHeroTitles.has(target.heroFanCompany?.title ?? '')) {
    target.heroFanCompany = cloneEditorValue(defaults.heroFanCompany);
  }
};

// Original (pre-redesign) hero titles per audience, matched so the published project's stale long
// titles get replaced with the new crisp hero copy. applicant => applicantPages, company => pages.
const legacyStaticHeroTitles: Partial<Record<StaticPageAudience, Partial<Record<StaticPageKey, string>>>> = {
  applicant: {
    pflege: 'Internationale Pflegefachkräfte für Einrichtungen, die verlässlich planen müssen.',
    gastro: 'Motivierte internationale Arbeitskräfte für Gastronomie und Hotellerie.',
    handwerk: 'Internationale Fachkräfte für Handwerksbetriebe mit langfristigem Personalbedarf.',
    einzelhandel: 'Personalgewinnung für Einzelhandel, Filialen und serviceorientierte Standorte.',
  },
  company: {
    pflege: 'Pflege in Deutschland braucht Menschen, die fachlich und menschlich ankommen.',
    gastro: 'Gastronomie und Hotellerie brauchen motivierte Teams.',
    handwerk: 'Handwerk lebt von Erfahrung, Vertrauen und Menschen, die bleiben.',
    einzelhandel: 'Einzelhandel braucht verlässliche Menschen im direkten Kundenkontakt.',
  },
};

const industryStaticPageKeys = new Set<StaticPageKey>(['pflege', 'gastro', 'handwerk', 'einzelhandel']);

const legacyGlobusJobTitles = new Set([
  'Stellenausschreibung: Bäcker:in',
  'Stellenausschreibung: Metzger:in',
  'Stellenausschreibung: Mitarbeiter:in an der Frischetheke',
  'Stellenausschreibung: Mitarbeiter:in in der Gastronomie',
  'İş ilanı: Fırıncı',
  'İş ilanı: Kasap',
  'İş ilanı: Taze ürün tezgâhı çalışanı',
  'İş ilanı: Gastronomi çalışanı',
  'Job opening: Baker',
  'Job opening: Butcher',
  'Job opening: Fresh-food counter team member',
  'Job opening: Hospitality team member',
  'Backe täglich frische Produkte mit echtem Handwerk',
  'Verbinde traditionelles Metzgerhandwerk mit moderner Produktion',
  'Berate Kund:innen und präsentiere Frische mit Qualität',
  'Bereite frische Speisen direkt für die Gäste zu',
  'Her gün gerçek el işçiliğiyle taze ürünler hazırla',
  'Geleneksel kasaplığı modern üretimle birleştir',
  'Müşterilere danışmanlık yap ve tazeliği kaliteyle sun',
  'Taze yemekleri doğrudan misafirler için hazırla',
  'Bake fresh products every day with genuine craftsmanship',
  'Combine traditional butchery with modern production',
  'Advise customers and present freshness with quality',
  'Prepare fresh meals directly for guests',
]);

const legacyGlobusJobEyebrows = new Set([
  'GLOBUS Meisterbäckerei',
  'GLOBUS Fachmetzgerei',
  'GLOBUS Frischetheke',
  'GLOBUS Gastronomie',
  'GLOBUS Usta Fırını',
  'GLOBUS Usta Kasabı',
  'GLOBUS taze ürün tezgâhı',
  'GLOBUS Gastronomi',
  'GLOBUS master bakery',
  'GLOBUS master butchery',
  'GLOBUS fresh-food counter',
  'GLOBUS hospitality',
  'Arbeiten in der GLOBUS Meisterbäckerei',
  'Arbeiten in der GLOBUS Fachmetzgerei',
  'Arbeiten an den GLOBUS Frischetheken',
  'Arbeiten im GLOBUS Restaurant',
  'GLOBUS Usta Fırınında çalışma',
  'GLOBUS Usta Kasabında çalışma',
  'GLOBUS taze ürün tezgâhlarında çalışma',
  'GLOBUS Restoranında çalışma',
  'Working in the GLOBUS master bakery',
  'Working in the GLOBUS master butchery',
  'Working at the GLOBUS fresh-food counters',
  'Working in the GLOBUS restaurant',
]);

const legacyGlobusVideoTitles = new Set([
  'Einblick in die Arbeit in der GLOBUS Bäckerei',
  'Einblick in die Arbeit als Metzger bei GLOBUS',
  'Einblick in die Arbeit an einer GLOBUS Frischetheke',
  'Einblick in die GLOBUS Gastronomie',
  'GLOBUS Usta Fırınındaki çalışmaya bir bakış',
  'GLOBUS’ta kasaplık işine bir bakış',
  'GLOBUS taze ürün tezgâhındaki çalışmaya bir bakış',
  'GLOBUS gastronomisine bir bakış',
  'A look at working in the GLOBUS master bakery',
  'A look at working as a butcher at GLOBUS',
  'A look at working at a GLOBUS fresh-food counter',
  'A look at working in GLOBUS hospitality',
]);

const legacyGlobusOverviewTitles = new Set([
  'Vier Arbeitsbereiche. Ein begleiteter Start in Deutschland.',
  'Dört çalışma alanı. Almanya’da desteklenen bir başlangıç.',
  'Four areas of work. One guided start in Germany.',
  'Vier Stellenausschreibungen bei GLOBUS',
  'GLOBUS’ta dört iş ilanı',
  'Four job openings at GLOBUS',
]);

const legacyGlobusOverviewEyebrows = new Set([
  'Deine Möglichkeiten bei GLOBUS',
  'GLOBUS’taki fırsatların',
  'Your opportunities at GLOBUS',
]);

const normalizeLegacyText = (value: string) =>
  value
    .normalize('NFKC')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[.!?]+$/u, '')
    .toLocaleLowerCase();

const matchesLegacyStaticHeroTitle = (value: string, audience: StaticPageAudience, pageKey: StaticPageKey) => {
  const legacyTitle = legacyStaticHeroTitles[audience]?.[pageKey];
  if (!legacyTitle) return false;

  const normalizedValue = normalizeLegacyText(value);
  const normalizedLegacy = normalizeLegacyText(legacyTitle);
  if (normalizedValue === normalizedLegacy) return true;

  if (audience === 'applicant' && pageKey === 'handwerk') {
    return normalizedValue.startsWith('internationale fachkräfte für handwerksbetriebe');
  }

  return false;
};

export const backfillStaticPageDefaults = (target: StaticPageContent, defaults: StaticPageContent, audience: StaticPageAudience, pageKey: StaticPageKey) => {
  if (pageKey === 'ablauf') {
    const legacyTitles = new Set([
      'Dein Weg nach Deutschland, Schritt für Schritt begleitet.',
      'Ein Prozess, der internationale Personalgewinnung steuerbar macht.',
      'İlk görüşmeden iş başlangıcına kadar net bir yol.',
      'Uluslararası personel kazanımını yönetilebilir kılan bir süreç.',
      'A clear path from the first conversation to starting work.',
      'A process that makes international recruitment manageable.',
    ]);
    if (legacyTitles.has(target.title) || /Viva\s*Porta[- ]?Medical/iu.test(target.intro)) {
      target.eyebrow = defaults.eyebrow;
      target.title = defaults.title;
      target.intro = defaults.intro;
    }
    if (!isEditorManagedImage(target.heroImage)) target.heroImage = defaults.heroImage;
  }

  if (pageKey === 'globus' && (target.heroGallery?.length ?? 0) > 1) {
    target.heroGallery = cloneEditorValue(defaults.heroGallery);
  }

  if (pageKey === 'globus') {
    const legacyCompanyGlobusTitles = new Set([
      'Globus Jobs mit Viva Porta.',
      'Viva Porta ile Globus işleri.',
      'Globus jobs with Viva Porta.',
    ]);
    const legacyCompanyGlobusCtaTitles = new Set([
      'Du möchtest bei Globus starten?',
      'Globus’ta çalışmak ister misin?',
      'Would you like to start at Globus?',
      'Du erkennst dich in einer dieser Rollen wieder?',
    ]);
    const hasLegacyCompanyPage = audience === 'company' && legacyCompanyGlobusTitles.has(target.title);
    const hasLegacyCompanyCta =
      audience === 'company' && Boolean(target.cta && legacyCompanyGlobusCtaTitles.has(target.cta.title));
    if (hasLegacyCompanyPage) {
      target.eyebrow = defaults.eyebrow;
      target.title = defaults.title;
      target.intro = defaults.intro;
      target.cards = cloneEditorValue(defaults.cards);
      target.sections = cloneEditorValue(defaults.sections);
      target.cta = cloneEditorValue(defaults.cta);
    } else if (hasLegacyCompanyCta) {
      target.cta = cloneEditorValue(defaults.cta);
    }

    const targetHeroImage = target.heroGallery?.[0];
    const defaultHeroImage = defaults.heroGallery?.[0];
    if (targetHeroImage && defaultHeroImage && !isEditorManagedImage(targetHeroImage.image)) {
      target.heroGallery = [cloneEditorValue(defaultHeroImage), ...(target.heroGallery?.slice(1) ?? [])];
    }

    const legacyJobSectionTitles = new Set([
      'Globus sucht Menschen für Frische, Handwerk und direkten Kundenkontakt.',
      'Globus tazelik, zanaat ve doğrudan müşteri teması için çalışan arıyor.',
      'Globus is looking for people for fresh food, skilled work and direct customer contact.',
    ]);
    target.sections = (target.sections ?? []).filter(section => !legacyJobSectionTitles.has(section.title));
    if (target.globusJobs?.length !== defaults.globusJobs?.length) {
      target.globusJobs = defaults.globusJobs ? cloneEditorValue(defaults.globusJobs) : undefined;
    }
    if (!defaults.globusJobsOverview) {
      target.globusJobsOverview = undefined;
    } else if (!target.globusJobsOverview) {
      target.globusJobsOverview = cloneEditorValue(defaults.globusJobsOverview);
    }
    if (
      target.globusJobsOverview &&
      defaults.globusJobsOverview &&
      legacyGlobusOverviewTitles.has(target.globusJobsOverview.title)
    ) {
      target.globusJobsOverview.title = defaults.globusJobsOverview.title;
    }
    if (
      target.globusJobsOverview &&
      defaults.globusJobsOverview &&
      legacyGlobusOverviewEyebrows.has(target.globusJobsOverview.eyebrow)
    ) {
      target.globusJobsOverview.eyebrow = defaults.globusJobsOverview.eyebrow;
    }
    target.globusJobs?.forEach((job, index) => {
      const defaultJob = defaults.globusJobs?.[index];
      if (!defaultJob) return;
      if (legacyGlobusJobTitles.has(job.title)) job.title = defaultJob.title;
      if (legacyGlobusJobEyebrows.has(job.eyebrow)) job.eyebrow = defaultJob.eyebrow;
      if (legacyGlobusVideoTitles.has(job.videoTitle)) job.videoTitle = defaultJob.videoTitle;
      const legacyLanguage = job.captionsLabel?.toLocaleLowerCase().includes('english')
        ? 'en'
        : job.captionsLabel?.toLocaleLowerCase().includes('türk')
          ? 'tr'
          : 'de';
      job.captionTracks = defaultJob.captionTracks.map(defaultTrack => {
        const storedTrack = job.captionTracks?.find(track => track.srclang === defaultTrack.srclang);
        return {
          ...cloneEditorValue(defaultTrack),
          ...cloneEditorValue(storedTrack ?? {}),
          src:
            storedTrack?.src ||
            (defaultTrack.srclang === legacyLanguage ? job.captionsSrc ?? '' : defaultTrack.src),
        };
      });
    });
  }

  if (pageKey === 'kontakt') {
    target.faq = [];
    const legacyContactTitles = new Set([
      'Stell uns deine Fragen zu deinem Weg nach Deutschland.',
      'Lassen Sie uns über Ihren Personalbedarf sprechen.',
      'Lass uns deinen Weg nach Deutschland gemeinsam klären.',
      'Lassen Sie uns Ihren Personalbedarf gemeinsam planen.',
      'Dein Weg nach Deutschland. Direkt geklärt.',
      'Ihr Personalbedarf. Direkt besprochen.',
    ]);
    if (legacyContactTitles.has(target.title)) {
      target.eyebrow = defaults.eyebrow;
      target.title = defaults.title;
      target.intro = defaults.intro;
      if (target.contact && defaults.contact) target.contact.text = defaults.contact.text;
    }
  }

  if (pageKey === 'ueber-vivaporta') {
    target.heroImage = defaults.heroImage;
    target.heroTitleHighlight = defaults.heroTitleHighlight;
    const hasEditableMissionStoryText = target.missionStory?.blocks?.some(block => block.paragraphs?.some(paragraph => typeof paragraph === 'object' && paragraph !== null && 'text' in paragraph));
    if (!target.missionStory || !hasEditableMissionStoryText) {
      target.missionStory = cloneEditorValue(defaults.missionStory);
    }

    const legacyMissionTitles = ['Wir begleiten Menschen, nicht nur Bewerbungen.', 'Wir verbinden internationale Gewinnung mit Verantwortung im Pflegealltag.'];
    if (legacyMissionTitles.includes(target.title)) {
      target.eyebrow = defaults.eyebrow;
      target.title = defaults.title;
      target.intro = defaults.intro;
    }

    for (const defaultSection of defaults.sections ?? []) {
      const hasSection = target.sections?.some(section => section.eyebrow === defaultSection.eyebrow || section.title === defaultSection.title);
      if (!hasSection) {
        target.sections = [...(target.sections ?? []), cloneEditorValue(defaultSection)];
      }
    }
  }

  if (pageKey === 'team' && Array.isArray(target.team) && Array.isArray(defaults.team)) {
    target.team.forEach(member => {
      const fallbackMember = defaults.team?.find(defaultMember => defaultMember.name === member.name);
      if (fallbackMember) syncTeamMemberDefaults(member, fallbackMember);
      syncRequestedTeamLinkedIn(member);
    });

    for (const defaultMember of defaults.team) {
      if (!target.team.some(member => member.name === defaultMember.name)) {
        target.team.push(cloneEditorValue(defaultMember));
      }
    }

    // Intentionally no section backfill here: the team page's content blocks (e.g. "Warum Viva Porta
    // anders …") may be removed in the editor. Re-adding missing default sections would resurrect a
    // block the user deleted, so the published sections list is left exactly as edited.
  }

  if (industryStaticPageKeys.has(pageKey) && isLegacyStaticHeroImage(target.heroImage)) {
    target.heroImage = defaults.heroImage;
  }

  if (industryStaticPageKeys.has(pageKey) && !target.heroStageTitle?.trim() && defaults.heroStageTitle) {
    target.heroStageTitle = defaults.heroStageTitle;
  }

  // Fill the hero video when missing, and replace a stale external placeholder loop with the current
  // default (e.g. the bundled Pflege video) so it shows on already-published pages too.
  const isEditorUploadedHeroVideo = target.heroVideo?.includes('/editor-assets/') || target.heroVideo?.startsWith('data:video/');
  const hasStaleHeroVideo = !target.heroVideo || !isEditorUploadedHeroVideo;

  if (industryStaticPageKeys.has(pageKey) && defaults.heroVideo && hasStaleHeroVideo) {
    target.heroVideo = defaults.heroVideo;
  }

  if (matchesLegacyStaticHeroTitle(target.title, audience, pageKey)) {
    target.eyebrow = defaults.eyebrow;
    target.title = defaults.title;
    target.intro = defaults.intro;
    target.heroStageTitle = defaults.heroStageTitle;
    target.heroHighlights = cloneEditorValue(defaults.heroHighlights);
    target.heroAction = cloneEditorValue(defaults.heroAction);
    target.heroImage = defaults.heroImage;
    target.heroVideo = defaults.heroVideo;
  }

  if (audience === 'company' && pageKey === 'einzelhandel' && !target.sections?.some(section => section.title === 'Frischetheken sind mehr als Besetzung: Sie prägen Frequenz, Beratung und Warenkorb.')) {
    const freshCounterSection = defaults.sections?.find(section => section.title === 'Frischetheken sind mehr als Besetzung: Sie prägen Frequenz, Beratung und Warenkorb.');
    if (freshCounterSection) target.sections = [...(target.sections ?? []), cloneEditorValue(freshCounterSection)];
  }

  if (audience === 'company' && pageKey === 'ablauf' && target.cards.length === 6 && target.cards[0]?.title === 'Bedarf und Rahmen klären') {
    target.cards = cloneEditorValue(defaults.cards);
  }
};
